import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { RawDate } from "app/UtilitiesAndConstants/types/dates";

type TimeDurationParts = {
    hours: number;
    minutes: number;
    seconds: number;
};

type DateParam = RawDate | Dayjs;

const dateTimeService = {
    MINUTES_IN_AN_HOUR: 60,
    SECONDS_IN_A_MINUTE: 60,
    SECONDS_IN_AN_HOUR: 3600,

    /**
     * Retrieves hours, minutes and seconds from given value
     *
     * @param duration - number of seconds
     * @example ```
     *  splitDurationIntoParts(59) // { hours: 0, minutes: 0, seconds: 59 }
     *  splitDurationIntoParts(345) // { hours: 0, minutes: 5, seconds: 45 }
     *  splitDurationIntoParts(3661) // { hours: 1, minutes: 1, seconds: 0 }
     * ```
     */
    splitDurationIntoParts(duration: number): TimeDurationParts {
        if (duration <= 0) {
            return {
                hours: 0,
                minutes: 0,
                seconds: 0,
            };
        }

        const hours = Math.floor(duration / this.SECONDS_IN_AN_HOUR);
        const minutes = Math.floor((duration - hours * this.SECONDS_IN_AN_HOUR) / this.MINUTES_IN_AN_HOUR);
        const seconds = Math.floor(duration - hours * this.SECONDS_IN_AN_HOUR - minutes * this.SECONDS_IN_A_MINUTE);

        return {
            hours,
            minutes,
            seconds,
        };
    },
    /**
     * Format date string using moment.js
     * @param date
     * @param format
     */
    formatWithMoment(date: DateParam, format: string): string {
        return dayjs(date).format(format);
    },
    formatWithUTCMoment(date: DateParam, format: string): string {
        return this.getUTCMoment(date).format(format);
    },
    getDateDiffFromNow(date: DateParam, unitOfTime: dayjs.OpUnitType) {
        return dayjs().diff(dayjs(date), unitOfTime);
    },
    getDateDiffFromNowLocal(date: DateParam, unitOfTime: dayjs.OpUnitType) {
        return dayjs().local().diff(dayjs(date).local(), unitOfTime);
    },
    getMoment(date: DateParam) {
        return dayjs(date);
    },
    getUTCMoment(date: DateParam): Dayjs {
        return dayjs.utc(date);
    },

    fromDateToCurrentDate(date1: DateParam, date2 = dayjs()): number {
        const days = dayjs(date1).diff(dayjs(date2), "day");
        return Math.abs(days);
    },

    getUTCTimeStamp(date: string) {
        const splitDate = date.split("-");
        return Date.UTC(parseInt(splitDate[0], 10), parseInt(splitDate[1], 10) - 1, parseInt(splitDate[2], 10));
    },
};

export default dateTimeService;
